// On account: money a player owes the organisation (F-POS-07; spec/06 §8.6).
//
// One receivable mechanism, two doors (the owner's model). A player's `receivable` balance at an
// organisation is what they owe; it arises from the **on-account checkout tender** (a door here) and
// from **postpaid sessions** (the other door, in the session spine). The balance is org-scoped — one
// per player across the organisation's venues — held in the ledger's `receivable` account (spec/06
// §2.1); the limit is org-scoped with an org→venue cap, and the whole feature is toggleable and
// **off by default** (`credit.enabled`, spec/06 §11) so no existing venue is changed the day it ships.
//
//   GET  …/players/{playerId}/credit                       the balance, limit and headroom (orders:read)
//   PUT  …/players/{playerId}/credit                       set the player's limit (credit:manage)
//   POST …/players/{playerId}/receivable/write-off         forgive what is owed (credit:write_off)
//   POST …/venues/{v}/players/{playerId}/receivable/settle  pay the receivable down (orders:create)
//   POST …/venues/{v}/sales/{id}/on-account                 the checkout tender (orders:on_account)
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PlayerIdSchema, SaleIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { SaleSchema } from "./commerce.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { CREDIT_MANAGE, CREDIT_WRITE_OFF, ORDERS_CREATE, ORDERS_ON_ACCOUNT, ORDERS_READ } from "../permissions/index.js";
const REASON = z.string().min(1).max(500);
/** A player's on-account position at the organisation (spec/06 §8.6). */
export const ReceivableSchema = z
    .object({
    playerId: PlayerIdSchema,
    /** Whether on account is enabled for the organisation (`credit.enabled`, off by default). */
    enabled: z.boolean(),
    /** What the player owes right now, never negative. */
    balance: NonNegativeMoneySchema,
    /** The effective credit limit — the per-player override or the resolved `credit.limitAmount`. */
    limit: NonNegativeMoneySchema,
    /** `limit − balance`, floored at zero: how much more may be put on account. */
    available: NonNegativeMoneySchema,
})
    .openapi('Receivable');
/** Set a player's credit limit (spec/06 §8.6). A limit of zero disables on account for the player. */
export const SetPlayerCreditSchema = z
    .object({ limit: NonNegativeMoneySchema })
    .openapi('SetPlayerCreditRequest');
/** Write a receivable off (spec/06 §8.6). Forgives an amount owed, with a reason; audited. */
export const WriteOffReceivableSchema = z
    .object({ amount: NonNegativeMoneySchema, reason: REASON })
    .openapi('WriteOffReceivableRequest');
/**
 * The tenders a receivable is paid down by at the venue (spec/06 §8.6): cash or counter UPI. Paying
 * it from the wallet is not offered yet, so this stays narrower than the sale tender.
 */
export const ReceivableSettlementTenderSchema = z
    .enum(['cash', 'counter_upi'])
    .openapi('ReceivableSettlementTender', { description: 'A counter tender that pays a receivable down (spec/06 §8.6).' });
/** Pay a receivable down by a tender (spec/06 §8.6). Cash or counter UPI at the venue. */
export const SettleReceivableSchema = z
    .object({ amount: NonNegativeMoneySchema, tender: ReceivableSettlementTenderSchema, reference: z.string().min(1).max(120).optional() })
    .openapi('SettleReceivableRequest');
/** Take a sale on account (spec/06 §8.6). Charges the outstanding (or a part) to the player's receivable. */
export const TakeSaleOnAccountSchema = z
    .object({ amount: NonNegativeMoneySchema.optional() })
    .openapi('TakeSaleOnAccountRequest');
const VenuePlayerCreditParamsSchema = VenueScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('VenuePlayerCreditParams');
const SaleOnAccountParamsSchema = VenueScopeParamsSchema.extend({ saleId: SaleIdSchema }).openapi('SaleOnAccountParams');
export const getPlayerCreditRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/players/{playerId}/credit',
    operationId: 'getPlayerCredit',
    tags: ['commerce'],
    summary: 'A player’s on-account position (F-POS-07)',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: VenuePlayerCreditParamsSchema },
    responses: {
        200: jsonResponse(ReceivableSchema, 'The player’s balance, limit and headroom.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such player at this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const setPlayerCreditRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/players/{playerId}/credit',
    operationId: 'setPlayerCredit',
    tags: ['commerce'],
    summary: 'Set a player’s credit limit (F-POS-07)',
    description: 'Sets the player’s on-account credit limit. Requires `credit:manage`. Audited.',
    [PERMISSION_EXTENSION]: CREDIT_MANAGE,
    request: {
        params: VenuePlayerCreditParamsSchema,
        body: { content: { 'application/json': { schema: SetPlayerCreditSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(ReceivableSchema, 'The player’s position, with the new limit.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such player at this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const writeOffReceivableRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/players/{playerId}/receivable/write-off',
    operationId: 'writeOffReceivable',
    tags: ['commerce'],
    summary: 'Write off a receivable (F-POS-07)',
    description: 'Forgives an amount the player owes, with a reason. Requires `credit:write_off`. Audited.',
    [PERMISSION_EXTENSION]: CREDIT_WRITE_OFF,
    request: {
        params: VenuePlayerCreditParamsSchema,
        body: { content: { 'application/json': { schema: WriteOffReceivableSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(ReceivableSchema, 'The player’s position after the write-off.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such player at this organisation.', 'not-found'),
        409: problemResponse('The amount exceeds what is owed.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const settleReceivableRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/players/{playerId}/receivable/settle',
    operationId: 'settleReceivable',
    tags: ['commerce'],
    summary: 'Pay a receivable down (F-POS-07)',
    description: 'Records a tender against what the player owes, into the venue’s drawer or day. Requires `orders:create`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: VenuePlayerCreditParamsSchema,
        body: { content: { 'application/json': { schema: SettleReceivableSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(ReceivableSchema, 'The player’s position after the payment.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such player at this organisation.', 'not-found'),
        409: problemResponse('The amount exceeds what is owed, or cash is taken by someone holding no open drawer (ADR-0031).', 'conflict', 'cash-needs-shift'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const takeSaleOnAccountRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/on-account',
    operationId: 'takeSaleOnAccount',
    tags: ['commerce'],
    summary: 'Take a sale on account (F-POS-07, F-POS-12)',
    description: 'Charges the outstanding amount (or a part of it) to the player’s receivable — the on-account ' +
        'checkout tender. The sale must name a registered player and stay within the credit limit. ' +
        'Requires `orders:on_account`. Audited; settles through the ledger.',
    [PERMISSION_EXTENSION]: ORDERS_ON_ACCOUNT,
    request: {
        params: SaleOnAccountParamsSchema,
        body: { content: { 'application/json': { schema: TakeSaleOnAccountSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleSchema, 'The sale after the on-account tender.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue.', 'not-found'),
        409: problemResponse('On account is off, the sale is paid or cancelled, or the credit limit would be exceeded.', 'conflict', 'credit-disabled', 'credit-limit-exceeded'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=credit.js.map